import { useEffect, useRef, useState } from 'react';

/** Binnen deze afstand van de bovenkant staat de balk altijd volledig uit. */
const BOVENGRENS = 24;

/**
 * Hoeveel er verschoven moet zijn voordat een richting telt. Zonder ondergrens
 * klapt de balk heen en weer bij het natrillen van een vinger, bij de rubber-band
 * van iOS en bij het inklappen van de adresbalk in mobiel Safari - die laatste
 * verplaatst de inhoud een paar pixels zonder dat de gebruiker scrolt.
 */
const DREMPEL = 6;

/**
 * Of de onderbalk moet krimpen: waar bij het scrollen naar beneden, onwaar aan de
 * bovenkant van de pagina en zodra er weer omhoog wordt gescrold.
 *
 * Dezelfde afspraak als in Instagram en Safari: naar beneden lezen betekent zoveel
 * mogelijk scherm, en de eerste veeg omhoog is het teken dat de bediening weer nodig is.
 *
 * @param gepauzeerd houdt de balk volledig, ongeacht het scrollen. Bedoeld voor als
 *   het Meer-paneel openstaat; dat legt de pagina stil, dus zonder pauze zou de balk
 *   in de gekrompen stand blijven hangen onder een paneel op ware grootte.
 * @param routeSleutel bij een andere waarde begint de meting opnieuw en staat de balk
 *   weer volledig uit. Een nieuwe pagina is een nieuw scrollverhaal.
 */
export const useNavKrimp = (gepauzeerd: boolean, routeSleutel: string): boolean => {
  const [gekrompen, setGekrompen] = useState(false);
  const vorigeY = useRef(0);
  const frameAangevraagd = useRef(false);

  useEffect(() => {
    setGekrompen(false);
    if (gepauzeerd) return;

    vorigeY.current = window.scrollY;

    const verwerk = () => {
      frameAangevraagd.current = false;

      // Buiten de echte scrollruimte klemmen: iOS laat scrollY negatief worden en
      // voorbij het einde doorschieten, en dat leest als een richting die er niet is.
      const maximum = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      const y = Math.min(Math.max(window.scrollY, 0), maximum);
      const verschil = y - vorigeY.current;

      if (y <= BOVENGRENS) {
        setGekrompen(false);
      } else if (Math.abs(verschil) > DREMPEL) {
        setGekrompen(verschil > 0);
      } else {
        // Te klein om iets te betekenen. vorigeY blijft juist staan, zodat een reeks
        // kleine stapjes samen wél de drempel haalt in plaats van eeuwig te verdampen.
        return;
      }

      vorigeY.current = y;
    };

    // Scrollen vuurt vaker dan er beelden getekend worden; één meting per frame is
    // genoeg en houdt de listener buiten het schilderwerk.
    const opScroll = () => {
      if (frameAangevraagd.current) return;
      frameAangevraagd.current = true;
      window.requestAnimationFrame(verwerk);
    };

    window.addEventListener('scroll', opScroll, { passive: true });
    return () => window.removeEventListener('scroll', opScroll);
  }, [gepauzeerd, routeSleutel]);

  return gekrompen;
};
